import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CImage } from '@coreui/react-pro'
import {
  capitalizeEachWord,
  convertDateToDotDate,
  convertSlashDateToHyphenDate,
  getAge,
} from '../../../../../utils/sharedFunctions'
import pccIcon from '../../../../../assets/icons/PCCIcon.svg'
import { calculatePatientIntegration } from '../../../../../components/common/shared/sharedPatientLogic'
import PanelEncTypeButtons from '../sharedPanelComponents/PanelEncTypeButtons'
import { saveEncounterField } from '../../../../../redux/sidePanel/sidePanelThunk'
import MobiScrollCalender from '../../../../../components/common/MobiScrollCalendar/MobiScrollCalendar'
import { dateUSFormat } from '../../../../../utils/dateTimeUtils'
import { reorderNameFirstToLastNoTitle } from '../../../Encounters/components/Utilities/Helpers'
import { calculateVisitStatus, VISIT_STATUSES } from './panelSummaryHelpers'
import PanelSummarySignButtons from './PanelSummarySignButtons'
import { debouncer, isAICPT, isAIDiagnosis } from '../sharedPanelComponents/sharedAIfunctions'
import { setEncounterField } from '../../../../../redux/sidePanel/sidePanelSlice'
import { getEncounterDefaults, getProceduresByCodes } from '../../../../../helper/backendHelper'

// get the visit before the current visit
function findLastVisit(currentEncId, encHistory = []) {
  for (let i = 0; i < encHistory?.length; i++) {
    const tempVisit = encHistory[i]
    if (tempVisit?._id === currentEncId) {
      const lastVisit = encHistory[i + 1]
      if (lastVisit) {
        if (new Date(tempVisit) < new Date(lastVisit)) {
          // alert ourselves if the dates being passed in are not already sorted
          console.error('Issue finding last visit')
        }
        return lastVisit
      } else {
        return null
      }
    }
  }
}

export default function PanelSummaryCard() {
  const dispatch = useDispatch()
  const censusDate = useSelector((state) => state.census.activeDate)

  const panelEncounter = useSelector((state) => state.sidePanel.panelEncounter)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const encounterType = useSelector((state) => state.sidePanel.panelEncounter?.encounterType)
  const dos = useSelector((state) => state.sidePanel.panelEncounter?.service_date_from)
  const isReadOnly = useSelector((state) => state.sidePanel.isReadOnly)
  const panelHistory = useSelector((state) => state.sidePanel.panelEncHistory) || []
  const openedEncounterId = useSelector((state) => state.sidePanel.panelChargeId)

  const lastVisit = findLastVisit(openedEncounterId, panelHistory)
  const { isConnected, connectedTitle } = calculatePatientIntegration(panelEncounter)

  const placesOfService = useSelector((state) => state.user.practice?.places_of_service) || []
  const currentPOS = placesOfService.find((pos) => pos.code === panelEncounter?.place_of_service)

  useEffect(() => {
    // if new, update to the current census date
    if (isLastEncounter && !isReadOnly) {
      dispatch(
        setEncounterField({
          field: 'service_date_from',
          value: censusDate,
          skipSaveLock: true,
        }),
      )
      // TODO
      // dispatch(
      //     saveEncounterField({
      //       field: 'encounterType',
      //       value: 'Subsequent',
      //     }),
      // )
    }
  }, [dispatch, isLastEncounter, censusDate, isReadOnly])

  const updateEncounter = async (field, value) => {
    await dispatch(
      setEncounterField({
        field,
        value,
      }),
    )
    debouncer(() => dispatch(saveEncounterField({})))
  }

  const setEncounterType = async (encounterType) => {
    let defaults = []
    try {
      const res = await getEncounterDefaults(panelEncounter?._id)
      if (res) {
        defaults = res
      }

      const encounterTypeDefault = defaults?.find((enc) => enc?.encounter_type === encounterType)

      if (encounterTypeDefault?.emcode && encounterTypeDefault?.emcode !== 'None') {
        const {
          results: [procedureDetails],
        } = await getProceduresByCodes([encounterTypeDefault?.emcode])
        const newEMCode = {
          code: encounterTypeDefault?.emcode,
          description: procedureDetails?.description,
          em_code: procedureDetails?.em_code,
          initial_code: procedureDetails?.initial_code,
          modifiers: [],
          rvu: procedureDetails?.rvu,
          units: 1,
        }

        let newCPTS = [...panelEncounter?.procedureCodes]
        if (newCPTS?.[0]?.em_code) {
          newCPTS[0] = newEMCode
        } else {
          newCPTS.unshift(newEMCode)
        }

        await updateEncounter('procedureCodes', newCPTS)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const visitStatus = calculateVisitStatus(isLastEncounter, panelEncounter?.status)

  return (
    <div id={'PanelSummaryCard'} className={'display-box'}>
      <div className={'d-flex justify-content-between'}>
        <div className={'d-flex flex-column'}>
          <div className={'d-flex dateStatus'}>
            <div className={'d-flex align-items-center gap-2'}>
              {convertDateToDotDate(panelEncounter?.service_date_from)}
              <MobiScrollCalender
                census
                disabled={isReadOnly}
                handleDateChange={async (date) => {
                  await updateEncounter('service_date_from', convertSlashDateToHyphenDate(date))
                }}
                value={dateUSFormat(dos)}
              />
            </div>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <div>{visitStatus === VISIT_STATUSES.NotSeen ? 'DOS Skipped' : visitStatus}</div>
          </div>
          <div className={'d-flex lastVisitDetails'}>
            Last visit: {convertDateToDotDate(lastVisit?.service_date_from)}
            {' - '}
            {lastVisit?.encounterType}
          </div>
          <PanelEncTypeButtons
            disabled={isReadOnly}
            encType={encounterType}
            setEncType={async (newEncType) => {
              await updateEncounter('encounterType', newEncType)
              if (panelEncounter?.coding_ai) {
                await setEncounterType(newEncType)
              }
            }}
          />
          <div className={'PanelEncTypeButtons'}>
            Rendering Provider:{' '}
            {reorderNameFirstToLastNoTitle(panelEncounter?.rendering_provider_name)}
            {/*  TODO eventually, make selectable*/}
          </div>
        </div>
        <PanelSummarySignButtons visitStatus={visitStatus} />
      </div>
      <hr style={{ margin: '0.5rem 0', width: '98%' }} />
      <div className={'d-flex flex-column me-3 patDetailWrapper'}>
        <div className={'d-flex justify-content-between align-items-center patientDetails'}>
          <div>
            <b>{panelEncounter?.patient_lastname?.toUpperCase()}</b>,{' '}
            {capitalizeEachWord(panelEncounter?.patient_firstname)}{' '}
            {capitalizeEachWord(panelEncounter?.patient_middlename)}
            {isConnected && (
              <CImage
                className="ms-2 mb-1"
                src={pccIcon}
                title={connectedTitle}
                width={24}
                height={24}
              />
            )}
          </div>
          <div className={'d-flex'}>
            {convertDateToDotDate(panelEncounter?.patient_dob)} (
            {getAge(panelEncounter?.patient_dob)}) yrs | {panelEncounter?.gender}
          </div>
        </div>
        <div className={'d-flex flex-wrap pt-1'}>
          {capitalizeEachWord(panelEncounter?.facility_name)}
          &nbsp;
          <div>
            &nbsp;|&nbsp;&nbsp;
            {`${
              currentPOS?.name ||
              panelEncounter?.place_of_service_name ||
              panelEncounter?.place_of_service
            }`}
          </div>
        </div>
        <div className={'d-flex justify-content-between '}>
          Diagnosis <CodeDisplay codeArray={panelEncounter?.diagnosisCodes} isDiagnosis />
        </div>
        <div className={'d-flex justify-content-between '}>
          Charges
          <CodeDisplay codeArray={panelEncounter?.procedureCodes} />
        </div>
      </div>
    </div>
  )
}

function addDiagnosisDot(dxCode) {
  if (!dxCode || dxCode?.length < 4) {
    return dxCode
  }
  return dxCode.slice(0, 3) + '.' + dxCode.slice(3)
}

const MAX_DX_DISPLAY_NUM_INDEX = 6
const MAX_CPT_DISPLAY_NUM_INDEX = 4

function CodeDisplay({ codeArray = [], isDiagnosis = false }) {
  const LIMIT = isDiagnosis ? MAX_DX_DISPLAY_NUM_INDEX : MAX_CPT_DISPLAY_NUM_INDEX
  const formatCode = (code) => (isDiagnosis ? addDiagnosisDot(code) : code)

  const features = useSelector((state) => state.user?.features) || []
  const coding_ai = useSelector((state) => state.sidePanel?.panelEncounter?.coding_ai)
  const aiDxCodes = useSelector((state) => state.sidePanel.panelEncounter?.aiDxCodes)

  return (
    <div className={'d-flex flex-column codeDisplay'}>
      {codeArray?.length === 0 ? (
        <div>{isDiagnosis ? 'No diagnoses selected' : 'No charges selected'}</div>
      ) : (
        <>
          <div
            className={`primaryCodeInfo ${
              (
                isDiagnosis
                  ? isAIDiagnosis(coding_ai, aiDxCodes, features, codeArray?.[0]?.code)
                  : isAICPT(coding_ai, features, codeArray?.[0]?.code)
              )
                ? 'ai'
                : ''
            }`}
          >
            <b className={'me-2'}>{formatCode(codeArray?.[0]?.code)}</b>
            <span title={codeArray?.[0]?.description}>{codeArray?.[0]?.description}</span>
          </div>
          <div className={'d-flex'}>
            {codeArray.map((item, index) => {
              if (index === 0 || index >= LIMIT) {
                return <React.Fragment key={item.code + index}></React.Fragment>
              }
              const mods = item?.modifiers?.join(', ')
              return (
                <div key={item.code + index} className={'me-2'}>
                  <b>{formatCode(item.code)}</b>
                  {mods?.length > 0 ? `:${mods}` : ''}
                </div>
              )
            })}
            {codeArray?.length > LIMIT && (
              <div>
                <i>+ {codeArray?.length - LIMIT} more</i>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
